export class AppStorage {
  /**
   * Initialize storage — migrates any leftover encrypted values to base64.
   * Called from APP_INITIALIZER. Clears stale encrypted tokens so the app
   * starts clean after removing the AES-256-GCM layer.
   */
  static async initialize(): Promise<void> {
    // Clean up any old encrypted values (salt.iv.ciphertext format)
    // and the session encryption key from the removed crypto layer.
    sessionStorage.removeItem('__szek__');

    const keysToClean: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null) keysToClean.push(key);
    }

    for (const key of keysToClean) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      // If value looks like old encrypted format (3 dot-separated segments,
      // none of which are valid JSON), remove it so auth redirects cleanly.
      const parts = raw.split('.');
      if (parts.length === 3 && !raw.startsWith('{') && !raw.startsWith('[')) {
        // Could be a JWT (also 3 dots) — check if middle segment decodes to JSON with "exp"
        try {
          const payload = JSON.parse(atob(parts[1]));
          if (payload && typeof payload === 'object' && 'exp' in payload) {
            continue; // It's a JWT, leave it alone
          }
        } catch {
          // Not a JWT — it's our old encrypted format, remove it
          localStorage.removeItem(key);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Token storage (base64 encoded)
  // ---------------------------------------------------------------------------

  static getTokenData(dataKey: string): string {
    try {
      const raw = localStorage.getItem(dataKey);
      if (!raw) return '';
      try {
        return decodeURIComponent(atob(raw));
      } catch {
        // Value couldn't be base64-decoded. Check if it's a raw JWT (3 dot-separated
        // segments where the middle decodes to a JSON object with "exp") — if so,
        // accept it. Otherwise it's corrupted; clear it and return empty string.
        const parts = raw.split('.');
        if (parts.length === 3) {
          try {
            const payload = JSON.parse(atob(parts[1]));
            if (payload && typeof payload === 'object' && 'exp' in payload) {
              return raw; // Raw JWT stored without base64 wrapper — accept it
            }
          } catch {
            // Not a JWT
          }
        }
        // Corrupted value — remove it so the app doesn't get stuck
        localStorage.removeItem(dataKey);
        return '';
      }
    } catch (error) {
      console.error('Error getting token data:', error);
      return '';
    }
  }

  static storeTokenData(dataKey: string, data: string): void {
    try {
      localStorage.setItem(dataKey, btoa(encodeURIComponent(data)));
    } catch (error) {
      console.error('Error storing token data:', error);
    }
  }

  static async getTokenDataAsync(dataKey: string): Promise<string> {
    return AppStorage.getTokenData(dataKey);
  }

  static async storeTokenDataAsync(dataKey: string, data: string): Promise<void> {
    AppStorage.storeTokenData(dataKey, data);
  }

  /**
   * Clears token-related data on logout.
   */
  static clearSecureData(): void {
    // Nothing extra to clear now that encryption is removed.
    // Token keys (et, ert, usif) are cleared individually by the logout flow.
  }

  // ---------------------------------------------------------------------------
  // Non-token localStorage helpers
  // ---------------------------------------------------------------------------

  static storeSessionData(key: string, data: unknown) {
    const jsonString = JSON.stringify(data);
    sessionStorage.setItem(key, btoa(encodeURIComponent(jsonString)));
  }

  static getSessionData(key: string): unknown | null {
    const encodedData = sessionStorage.getItem(key);
    if (encodedData) {
      const data = decodeURIComponent(atob(encodedData));
      return data ? JSON.parse(data) : null;
    }
    return null;
  }

  static removeSessionData(key: string) {
    sessionStorage.removeItem(key);
  }

  static storeLocalStorageData(key: string, data: unknown): void {
    const jsonString = JSON.stringify(data);
    localStorage.setItem(key, btoa(encodeURIComponent(jsonString)));
  }

  static storeLocalStorageDataDefault(key: string, data: unknown) {
    const jsonString = JSON.stringify(data);
    localStorage.setItem(key, jsonString);
  }

  static storeLocalStorageDataRaw(key: string, data: string | number | boolean) {
    localStorage.setItem(key, data.toString());
  }

  static getLocalStorageDataRaw(key: string) {
    return localStorage.getItem(key);
  }

  static getLocalStorageData(key: string) {
    const encodedData = localStorage.getItem(key);
    if (encodedData) {
      try {
        const data = decodeURIComponent(atob(encodedData));
        return data ? JSON.parse(data) : null;
      } catch {
        // Corrupted or old encrypted value — remove it
        localStorage.removeItem(key);
        return null;
      }
    }
    return null;
  }

  static getLocalStorageDataDefault(key: string) {
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  }
}
